import { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import { DndContext, closestCenter } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// MUI Components
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import { alpha } from '@mui/material/styles';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QuizIcon from '@mui/icons-material/Quiz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useSurveyTranslation, useCommonTranslation } from '@/i18n/hooks/useTypedTranslation';

import { publicSurveyService } from '@/services/surveyService';
import { shouldShowQuestion, validatePhase, calculateSurveyProgress, type AnswerData, type ValidationError } from '@/utils/surveyHelpers';
import { useIsTouchDevice, useSortableSensors, useRespondentIdentity, useDraftManager } from '@/hooks';
import type { Survey, Question, QuestionOption, QuestionCategory } from '@/types';

// Survey UX Components
import { ValidationSummaryModal } from '@/components/survey/ValidationSummaryModal';
import { SurveyProgressBar } from '@/components/survey/SurveyProgressBar';
import { DraftRecoveryModal } from '@/components/survey/DraftRecoveryModal';
import { FloatingSaveButton } from '@/components/survey/FloatingSaveButton';

export function PublicSurvey() {
  const { token } = useParams<{ token: string }>();
  const { i18n } = useTranslation();
  const { t } = useSurveyTranslation();
  const { t: tCommon } = useCommonTranslation();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Submission status state
  const [submissionStatus, setSubmissionStatus] = useState<{
    hasSubmitted: boolean;
    allowMultipleSubmissions: boolean;
    canSubmit: boolean;
  } | null>(null);

  // Form state
  const [answers, setAnswers] = useState<Record<number, AnswerData>>({});
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [validationErrorsList, setValidationErrorsList] = useState<ValidationError[]>([]);
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Scroll-based section tracking
  const [visibleSectionIndex, setVisibleSectionIndex] = useState(0);
  const sectionRefs = useRef<Map<number, HTMLElement>>(new Map());

  // Draft management state
  const [showDraftRecoveryModal, setShowDraftRecoveryModal] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);

  // Track newly visible conditional questions for auto-scroll and highlight
  const previousVisibleQuestionIdsRef = useRef<Set<number>>(new Set());
  const [highlightedQuestionId, setHighlightedQuestionId] = useState<number | null>(null);

  // Get respondent identity (anonymous token for draft management)
  const { anonymousToken } = useRespondentIdentity(token || '');

  // Draft manager hook - manual save only (no auto-save)
  const {
    saveStatus,
    lastSavedAt,
    hasDraft,
    draftData,
    isLoading: draftLoading,
    hasUnsavedChanges,
    markAsChanged,
    saveManually,
    deleteDraft,
    resetUnsavedChanges,
  } = useDraftManager(token || '', anonymousToken);

  const loadSurvey = useCallback(async () => {
    try {
      const data = await publicSurveyService.getSurvey(token!);

      // Debug logging for TRUE/FALSE questions
      console.log('[PublicSurvey] Survey loaded:', {
        surveyId: data.id,
        title: data.title,
        questionCount: data.questions?.length,
      });

      // Log ALL questions to see their structure
      console.log('[PublicSurvey] All questions:', data.questions?.map(q => ({
        id: q.id,
        text: q.text,
        questionTypeId: q.questionTypeId,
        questionTypeCode: q.questionTypeCode, // Use flat property from public API
        optionsCount: q.options?.length,
        hasOptions: !!q.options && q.options.length > 0,
      })));

      const trueFalseQuestions = data.questions?.filter(q => q.questionTypeCode === 'TRUE_FALSE');
      console.log('[PublicSurvey] TRUE/FALSE filter result:', {
        totalQuestions: data.questions?.length,
        trueFalseCount: trueFalseQuestions?.length,
        foundAny: trueFalseQuestions && trueFalseQuestions.length > 0,
      });

      if (trueFalseQuestions && trueFalseQuestions.length > 0) {
        console.log('[PublicSurvey] TRUE/FALSE questions found:', trueFalseQuestions.length);
        trueFalseQuestions.forEach(q => {
          console.log(`[PublicSurvey] Question ${q.id} ("${q.text}"):`, {
            questionTypeCode: q.questionTypeCode,
            optionsCount: q.options?.length,
            options: q.options?.map(opt => ({
              id: opt.id,
              label: opt.label,
              code: opt.code,
              numericValue: opt.numericValue,
              displayOrder: opt.displayOrder,
            })),
          });
        });
      }

      setSurvey(data);

      // Initialize answers
      const initialAnswers: Record<number, AnswerData> = {};
      if (data.questions) {
        data.questions.forEach((q) => {
          initialAnswers[q.id] = {
            questionId: q.id,
            freeText: '',
            selectedOptionIds: [],
            rankedSelections: [],
          };
        });
      }
      setAnswers(initialAnswers);
    } catch {
      setError('Survey not found or no longer available');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadSurvey();
    }
  }, [token, loadSurvey]);

  // Check submission status after survey loads
  useEffect(() => {
    const checkSubmissionStatus = async () => {
      if (!token || !survey) return;

      try {
        const status = await publicSurveyService.checkSubmissionStatus(token, anonymousToken || undefined);
        setSubmissionStatus(status);

        console.log('[PublicSurvey] Submission status:', {
          hasSubmitted: status.hasSubmitted,
          allowMultipleSubmissions: status.allowMultipleSubmissions,
          canSubmit: status.canSubmit,
        });
      } catch (err) {
        console.error('[PublicSurvey] Failed to check submission status:', err);
        // Don't block the survey if the check fails
        setSubmissionStatus(null);
      }
    };

    checkSubmissionStatus();
  }, [token, survey, anonymousToken]);

  // Change language to survey's default language when survey loads
  useEffect(() => {
    if (survey?.defaultLanguage && survey.defaultLanguage !== i18n.language) {
      i18n.changeLanguage(survey.defaultLanguage);
    }
  }, [survey?.defaultLanguage, i18n]);

  // Check for existing draft and show recovery modal
  useEffect(() => {
    if (!draftLoading && !draftChecked && survey) {
      setDraftChecked(true);
      if (hasDraft && draftData) {
        setShowDraftRecoveryModal(true);
      }
    }
  }, [draftLoading, draftChecked, survey, hasDraft, draftData]);

  // Handle draft resume - restore answers from draft
  const handleResumeDraft = useCallback(() => {
    if (draftData?.answers) {
      setAnswers(draftData.answers);
    }
    setShowDraftRecoveryModal(false);
  }, [draftData]);

  // Handle start over - delete draft and start fresh
  const handleStartOver = useCallback(async () => {
    await deleteDraft();
    setShowDraftRecoveryModal(false);
  }, [deleteDraft]);

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const questions = useMemo(() => survey?.questions || [], [survey]);

  // Show all questions on a single page (no section/phase navigation)
  const allQuestions = useMemo(() => {
    return [...questions].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [questions]);

  // Calculate estimated time (roughly 20 seconds per question)
  // Exclude SECTION types - they're structural elements, not answerable questions
  const answerableQuestions = useMemo(() => {
    return questions.filter(q => {
      const typeCode = q.questionTypeCode || q.questionType?.code;
      return typeCode !== 'SECTION';
    });
  }, [questions]);
  const totalQuestions = answerableQuestions.length;
  const estimatedMinutes = Math.max(1, Math.ceil(totalQuestions / 3));

  // Filter visible questions based on conditions
  // Note: allQuestions is derived from questions, so we only need allQuestions in deps
  const visibleQuestions = useMemo(() => {
    return allQuestions.filter((q) => shouldShowQuestion(q, allQuestions, answers));
  }, [allQuestions, answers]);

  // Auto-scroll to newly visible conditional questions
  useEffect(() => {
    if (!survey || visibleQuestions.length === 0) return;

    const currentVisibleIds = new Set(visibleQuestions.map(q => q.id));
    const previousVisibleIds = previousVisibleQuestionIdsRef.current;

    // Find newly visible questions (questions that weren't visible before)
    const newlyVisibleQuestions = visibleQuestions.filter(
      q => !previousVisibleIds.has(q.id)
    );

    // Only auto-scroll if:
    // 1. There are newly visible questions
    // 2. The survey has already loaded (not the initial render)
    // 3. User has started answering (prevents scroll on page load)
    if (newlyVisibleQuestions.length > 0 && previousVisibleIds.size > 0) {
      // Get the first newly visible question
      const firstNewQuestion = newlyVisibleQuestions[0];

      // Small delay to ensure DOM has updated
      setTimeout(() => {
        const element = document.getElementById(`question-${firstNewQuestion.id}`);
        if (element) {
          // Scroll with smooth animation
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          // Highlight the question for 2 seconds
          setHighlightedQuestionId(firstNewQuestion.id);
          setTimeout(() => setHighlightedQuestionId(null), 2000);
        }
      }, 100);
    }

    // Update the ref for next comparison
    previousVisibleQuestionIdsRef.current = currentVisibleIds;
  }, [visibleQuestions, survey]);

  // Handle manual save - saves current answers (defined after visibleQuestions)
  const handleSaveNow = useCallback(async () => {
    const currentIndex = visibleQuestions.length > 0 ? visibleQuestions.length - 1 : 0;

    // Debug logging for save operation
    console.log('[PublicSurvey] handleSaveNow called:', {
      hasToken: !!token,
      tokenValue: token,
      hasAnonymousToken: !!anonymousToken,
      anonymousTokenValue: anonymousToken,
      answersCount: Object.keys(answers).length,
      currentIndex,
      totalQuestions: visibleQuestions.length,
      visibleQuestionsIds: visibleQuestions.map(q => q.id),
    });

    await saveManually(answers as Parameters<typeof saveManually>[0], currentIndex, visibleQuestions.length);
  }, [saveManually, answers, visibleQuestions, token, anonymousToken]);

  // Get categories from survey
  const categories = useMemo(() => survey?.categories || [], [survey]);

  // Compute hierarchical question numbers (e.g., 1.1, 1.2, 2.1, 2.1.1 for conditional questions)
  const questionNumbers = useMemo(() => {
    const numbers: Record<number, string> = {};
    const sortedCategories = [...categories].sort((a, b) => a.displayOrder - b.displayOrder);

    // Separate parent questions (no displayConditions) from conditional questions
    const isConditionalQuestion = (q: Question) =>
      q.displayConditions && q.displayConditions.length > 0;

    const parentQuestions = allQuestions.filter(q => !isConditionalQuestion(q));
    const conditionalQuestions = allQuestions.filter(q => isConditionalQuestion(q));

    // Group parent questions by category
    const questionsByCategory: Record<number | 'general', Question[]> = { general: [] };
    sortedCategories.forEach((cat) => {
      questionsByCategory[cat.id] = [];
    });

    parentQuestions.forEach((q) => {
      if (q.categoryId && questionsByCategory[q.categoryId]) {
        questionsByCategory[q.categoryId].push(q);
      } else {
        questionsByCategory.general.push(q);
      }
    });

    // First pass: Assign numbers to parent questions (section.questionIndex)
    let sectionIndex = 0;

    // Handle questions without a category (General section)
    if (questionsByCategory.general.length > 0) {
      sectionIndex++;
      questionsByCategory.general.forEach((q, idx) => {
        numbers[q.id] = `${sectionIndex}.${idx + 1}`;
      });
    }

    // Handle each category in order
    sortedCategories.forEach((cat) => {
      const catQuestions = questionsByCategory[cat.id];
      if (catQuestions.length > 0) {
        sectionIndex++;
        catQuestions.forEach((q, idx) => {
          numbers[q.id] = `${sectionIndex}.${idx + 1}`;
        });
      }
    });

    // Second pass: Assign hierarchical numbers to conditional questions
    const childrenByParent: Record<number, Question[]> = {};
    conditionalQuestions.forEach((q) => {
      const parentId = q.displayConditions![0].dependsOnQuestionId;
      if (!childrenByParent[parentId]) {
        childrenByParent[parentId] = [];
      }
      childrenByParent[parentId].push(q);
    });

    // Assign sub-numbers to each conditional question
    Object.entries(childrenByParent).forEach(([parentIdStr, children]) => {
      const parentId = Number(parentIdStr);
      const parentNumber = numbers[parentId];

      if (parentNumber) {
        children.sort((a, b) => a.displayOrder - b.displayOrder);
        children.forEach((child, idx) => {
          numbers[child.id] = `${parentNumber}.${idx + 1}`;
        });
      } else {
        children.forEach((child, idx) => {
          numbers[child.id] = `?.${idx + 1}`;
        });
      }
    });

    return numbers;
  }, [allQuestions, categories]);

  // Group visible questions by category while preserving display order
  // Returns array of { category: QuestionCategory | null, questions: Question[] }
  const groupedQuestions = useMemo(() => {
    const groups: { category: QuestionCategory | null; questions: Question[] }[] = [];
    let currentCategoryId: number | null | undefined = undefined;
    let currentGroup: { category: QuestionCategory | null; questions: Question[] } | null = null;

    visibleQuestions.forEach((q) => {
      const categoryId = q.categoryId ?? null;

      // If category changed, start a new group
      if (categoryId !== currentCategoryId) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        const category = categoryId ? categories.find(c => c.id === categoryId) || null : null;
        currentGroup = { category, questions: [q] };
        currentCategoryId = categoryId;
      } else {
        // Same category, add to current group
        currentGroup?.questions.push(q);
      }
    });

    // Push the last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [visibleQuestions, categories]);

  // Intersection Observer to track which section is visible while scrolling
  useEffect(() => {
    const sectionsWithCategories = groupedQuestions
      .map((g, idx) => ({ group: g, index: idx }))
      .filter((item) => item.group.category !== null);

    if (sectionsWithCategories.length === 0) return;

    // Create observer with threshold and rootMargin to detect section at top of viewport
    // Account for fixed progress bar height (~60px)
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible section
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // Get the section closest to the top
          const topEntry = visibleEntries.reduce((prev, curr) => {
            return prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr;
          });
          const sectionIndex = parseInt(topEntry.target.getAttribute('data-section-index') || '0', 10);
          setVisibleSectionIndex(sectionIndex);
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px', // Account for fixed header, trigger when section is in upper 40%
        threshold: 0,
      }
    );

    // Observe all section headers
    sectionRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [groupedQuestions]);

  // Calculate survey progress for the progress bar
  // Uses useMemo to avoid recalculation on every render
  const surveyProgress = useMemo(() => {
    return calculateSurveyProgress(answers, visibleQuestions);
  }, [answers, visibleQuestions]);

  // Get current section info for progress bar display based on scroll position
  const currentSectionInfo = useMemo(() => {
    if (groupedQuestions.length === 0) return null;

    // Filter to only groups with categories
    const sectionsWithCategories = groupedQuestions
      .map((g, idx) => ({ group: g, index: idx }))
      .filter((item) => item.group.category !== null);

    const totalSections = sectionsWithCategories.length;
    if (totalSections === 0) return null;

    // Use the scroll-based visible section index
    const currentGroup = groupedQuestions[visibleSectionIndex];

    // Find the 0-based section index (counting only sections with categories)
    // The progress bar component will add +1 for display
    const sectionIndex = sectionsWithCategories.findIndex(
      (item) => item.index === visibleSectionIndex
    );

    return {
      name: currentGroup?.category?.name || 'Questions',
      index: Math.max(0, sectionIndex), // 0-based index, clamp to 0 if not found
      total: totalSections,
    };
  }, [groupedQuestions, visibleSectionIndex]);

  const updateAnswer = useCallback((questionId: number, data: Partial<AnswerData>) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], ...data },
    }));

    // Mark that there are unsaved changes (no auto-save)
    markAsChanged();

    // Clear validation error when user interacts
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  }, [markAsChanged]);

  const handleSubmit = async () => {
    if (!token || !survey) return;

    // Validate all visible questions
    const { isValid, errors } = validatePhase(visibleQuestions, answers, questions);

    if (!isValid) {
      // Store errors in both formats:
      // 1. errorMap for inline display on question cards (uses translated message)
      // 2. errors array for the modal (uses translation keys)
      const errorMap: Record<number, string> = {};
      errors.forEach((err) => {
        const translated = err.params
          ? t(err.messageKey as never, err.params as never)
          : t(err.messageKey as never);
        // Use translated message if available, otherwise fall back to English
        errorMap[err.questionId] = (translated && translated !== err.messageKey)
          ? String(translated)
          : err.fallbackMessage;
      });
      setValidationErrors(errorMap);
      setValidationErrorsList(errors);

      // Show validation summary modal instead of just scrolling
      setShowValidationModal(true);
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: Create the response record
      const responseRecord = await publicSurveyService.startResponse(token);

      // Step 2: Transform answers to backend format and submit
      const submitData = {
        answers: Object.values(answers).map((a) => {
          // Convert selectedOptionIds and rankedSelections to selectedOptions format
          const selectedOptions: { optionId: number; rank?: number }[] = [];

          // Add ranked selections with their ranks
          // The "Other" option (if selected) uses its real database ID and is included
          // The "other text" is also stored in freeText field for backward compatibility
          if (a.rankedSelections && a.rankedSelections.length > 0) {
            a.rankedSelections
              .filter((rs) => rs.optionId > 0) // Sanity check for valid option IDs
              .forEach((rs) => {
                selectedOptions.push({ optionId: rs.optionId, rank: rs.rank });
              });
          }
          // Add regular option selections (no rank)
          else if (a.selectedOptionIds && a.selectedOptionIds.length > 0) {
            a.selectedOptionIds.forEach((optionId) => {
              selectedOptions.push({ optionId });
            });
          }

          // Include "other text" when "Other" option is selected
          const freeText = a.isOtherSelected ? a.otherText : a.freeText;

          return {
            questionId: a.questionId,
            freeText: freeText || undefined,
            selectedOptions,
          };
        }),
      };

      await publicSurveyService.submitResponse(token, responseRecord.id, submitData);
      setSubmitted(true);
      resetUnsavedChanges(); // Clear unsaved state after successful submission
    } catch (err: unknown) {
      // Extract error message from API response or use fallback
      let errorMessage = 'Failed to submit response. Please try again.';

      const error = err as { response?: { data?: { message?: string } }; message?: string };
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            {t('public.loading.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('public.loading.description')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error State
  if (error && !survey) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ py: 6 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: alpha('#ef4444', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 36, color: 'error.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {tCommon('status.error')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('public.error.notFound')}
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary">
              If you believe this is an error, please contact the survey administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Success State
  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
          <Box sx={{ height: 4, bgcolor: 'success.main' }} />
          <CardContent sx={{ py: 6, px: { xs: 3, sm: 4 } }}>
            {/* Logo - Clickable to home */}
            <Box
              component="a"
              href="/"
              sx={{
                display: 'inline-block',
                mb: 3,
                transition: 'opacity 0.2s ease',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="NLPask"
                sx={{
                  height: { xs: 40, sm: 48 },
                  width: 'auto',
                }}
              />
            </Box>

            {/* Success Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha('#10b981', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main' }} />
            </Box>

            {/* Thank You Message */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {t('public.success.title')}
            </Typography>
            {survey?.completionMessage ? (
              <Box
                sx={{
                  mb: 4,
                  color: 'text.secondary',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  '& p': { my: 0.5 },
                  '& ul': { pl: 3, my: 0.5, listStyleType: 'disc' },
                  '& ol': { pl: 3, my: 0.5, listStyleType: 'decimal' },
                  '& li': { mb: 0.25, display: 'list-item' },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(survey.completionMessage, { FORBID_ATTR: ['style'] }) }}
              />
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t('public.success.message')}
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Platform Description */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                Discover the power of NLPask
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                Create professional surveys, quizzes, and exams powered by AI-driven analytics.
                Get actionable insights from your data with natural language processing.
              </Typography>
              <Button
                variant="contained"
                href="/"
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                Explore NLPask
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Chip
              label={t('public.success.badge')}
              size="small"
              sx={{ bgcolor: alpha('#14B8A6', 0.1), color: 'primary.main' }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!survey) return null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 11, sm: 10 } }}>
      {/* Survey Progress Bar - Fixed at top while scrolling */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <SurveyProgressBar
          progress={surveyProgress}
          currentSection={currentSectionInfo?.name}
          totalSections={currentSectionInfo?.total}
          currentSectionIndex={currentSectionInfo?.index}
          showSectionInfo={currentSectionInfo !== null && currentSectionInfo.total > 1}
          showEstimatedTime={true}
        />
      </Box>

      {/* Add padding-top to account for fixed progress bar height (~60px) */}
      <Container maxWidth="md" sx={{ pt: { xs: 9, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        {/* Survey Header */}
        <Card sx={{ mb: 2 }}>
          <Box sx={{ height: 4, bgcolor: 'primary.main' }} />
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Logo and Language Switcher row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              {/* Logo with home link */}
              <Box
                component="a"
                href="https://nlpask.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  '&:hover': { opacity: 0.8 },
                  transition: 'opacity 0.2s ease',
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="NLPask"
                  sx={{
                    height: { xs: 28, sm: 32 },
                    width: 'auto',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Chip
                icon={<QuizIcon sx={{ fontSize: 14 }} />}
                label={t('public.header.badge')}
                size="small"
                sx={{
                  bgcolor: alpha('#14B8A6', 0.1),
                  color: 'primary.main',
                  fontWeight: 500,
                  height: 24,
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 1,
                textAlign: 'center',
                // Responsive font size for very small screens
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                wordBreak: 'break-word',
              }}
            >
              {survey.title}
            </Typography>

            {survey.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  textAlign: { xs: 'left', sm: 'justify' },
                  wordBreak: 'break-word',
                }}
              >
                {survey.description}
              </Typography>
            )}

            {/* Instructions - shown directly under description with subtle styling */}
            {survey.instructions && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontStyle: 'italic',
                  pl: 2,
                  borderLeft: 3,
                  borderLeftColor: 'primary.main',
                  textAlign: { xs: 'left', sm: 'justify' },
                  wordBreak: 'break-word',
                }}
              >
                {survey.instructions}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, sm: 3 }, color: 'text.secondary', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{estimatedMinutes} min</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <QuizIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{totalQuestions} questions</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Submission Status Messages */}
        {submissionStatus && submissionStatus.hasSubmitted && (
          <>
            {/* Blocking message when user already submitted and can't resubmit */}
            {!submissionStatus.allowMultipleSubmissions && (
              <Alert
                severity="warning"
                icon={<InfoOutlinedIcon />}
                sx={{
                  mb: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('public.submission.alreadySubmitted.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('public.submission.alreadySubmitted.message')}
                </Typography>
              </Alert>
            )}

            {/* Info banner when user can resubmit */}
            {submissionStatus.allowMultipleSubmissions && (
              <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{
                  mb: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('public.submission.previouslySubmitted.message')}
                </Typography>
              </Alert>
            )}
          </>
        )}

        {/* Questions grouped by sections - only show if user can submit */}
        {(!submissionStatus || submissionStatus.canSubmit) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {groupedQuestions.map((group, groupIndex) => {
            return (
              <Box key={group.category?.id ?? `uncategorized-${groupIndex}`}>
                {/* Section Header - compact */}
                {group.category && (
                  <Box
                    ref={(el: HTMLElement | null) => {
                      if (el) {
                        sectionRefs.current.set(groupIndex, el);
                      } else {
                        sectionRefs.current.delete(groupIndex);
                      }
                    }}
                    data-section-index={groupIndex}
                    sx={{
                      mt: groupIndex > 0 ? 2 : 0,
                      mb: 1.5,
                      pb: 1,
                      borderBottom: 2,
                      borderColor: 'primary.main',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: alpha('#14B8A6', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        <FolderOpenIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.3 }}>
                          {group.category.name}
                        </Typography>
                        {group.category.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              lineHeight: 1.6,
                              wordBreak: 'break-word',
                            }}
                          >
                            {group.category.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Questions in this section - compact gap */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {group.questions.map((question) => (
                    <QuestionField
                      key={question.id}
                      question={question}
                      questionId={question.id}
                      questionNumber={questionNumbers[question.id] || '?'}
                      answer={answers[question.id]}
                      error={validationErrors[question.id]}
                      onAnswerChange={updateAnswer}
                      isHighlighted={highlightedQuestionId === question.id}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}

          {visibleQuestions.length === 0 && (
            <Card>
              <CardContent sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No questions available.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
        )}
      </Container>

      {/* Floating Save Button - shows when there are unsaved changes */}
      <FloatingSaveButton
        hasUnsavedChanges={hasUnsavedChanges}
        saveStatus={saveStatus}
        lastSavedAt={lastSavedAt}
        onSave={handleSaveNow}
        bottom={100} // Above the submit bar
        right={24}
      />

      {/* Fixed Bottom Submit Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2 },
          zIndex: 1000,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* Submit Button */}
            <Badge
              badgeContent={Object.keys(validationErrors).length}
              color="error"
              invisible={Object.keys(validationErrors).length === 0}
              sx={{
                '& .MuiBadge-badge': {
                  right: -4,
                  top: -4,
                  fontWeight: 700,
                },
              }}
            >
              <Button
                variant="contained"
                size="medium"
                onClick={handleSubmit}
                disabled={submitting || (submissionStatus?.hasSubmitted && !submissionStatus?.allowMultipleSubmissions)}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                sx={{ minWidth: { xs: 120, sm: 180 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                {submitting ? t('public.submit.submitting') : t('public.submit.button')}
              </Button>
            </Badge>
          </Box>
        </Container>
      </Box>

      {/* Validation Summary Modal */}
      <ValidationSummaryModal
        open={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        errors={validationErrorsList}
        questions={visibleQuestions}
        onNavigateToQuestion={(questionId) => {
          document.getElementById(`question-${questionId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          setShowValidationModal(false);
        }}
      />

      {/* Draft Recovery Modal */}
      <DraftRecoveryModal
        open={showDraftRecoveryModal}
        onResume={handleResumeDraft}
        onStartOver={handleStartOver}
        onClose={() => setShowDraftRecoveryModal(false)}
        lastSavedAt={lastSavedAt}
        completionPercentage={draftData ? Math.round((Object.values(draftData.answers).filter(a =>
          a.freeText || (a.selectedOptionIds && a.selectedOptionIds.length > 0) || (a.rankedSelections && a.rankedSelections.length > 0)
        ).length / draftData.totalQuestions) * 100) : 0}
      />
    </Box>
  );
}

// Question Field Component - Memoized for performance
interface QuestionFieldProps {
  question: Question;
  questionId: number;
  questionNumber?: string; // Kept for backwards compatibility but no longer displayed
  answer: AnswerData;
  error?: string;
  onAnswerChange: (questionId: number, data: Partial<AnswerData>) => void;
  isHighlighted?: boolean;
}

const QuestionField = memo(function QuestionField({ question, questionId, answer, error, onAnswerChange, isHighlighted = false }: QuestionFieldProps) {
  // Create stable onChange callback that includes questionId
  const onChange = useCallback(
    (data: Partial<AnswerData>) => onAnswerChange(questionId, data),
    [questionId, onAnswerChange]
  );
  const typeCode = question.questionTypeCode || question.questionType?.code;
  const allOptions = question.options || [];
  // Filter out the "Other" option from regular display - it has its own dedicated UI
  const options = allOptions.filter(o => !o.isOtherOption);
  const otherOptionId = allOptions.find(o => o.isOtherOption)?.id;
  const metadata = question.metadata as Record<string, unknown> | undefined;

  // SECTION type - render as a section header, not an interactive question
  if (typeCode === 'SECTION') {
    return (
      <Card
        id={`question-${question.id}`}
        sx={{
          bgcolor: alpha('#14B8A6', isHighlighted ? 0.12 : 0.03),
          borderLeft: 6,
          borderLeftColor: '#14B8A6',
          boxShadow: isHighlighted ? 4 : 1,
          transition: 'all 0.3s ease-in-out',
          ...(isHighlighted && {
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(20, 184, 166, 0.7)' },
              '50%': { boxShadow: '0 0 0 8px rgba(20, 184, 166, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(20, 184, 166, 0)' },
            },
            animation: 'pulse 1.5s ease-in-out',
          }),
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: question.helpText ? 0.5 : 0,
            }}
          >
            {question.text}
          </Typography>
          {question.helpText && (
            <Box
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: 'text.secondary',
                textAlign: 'left !important',
                '& *': { textAlign: 'left !important' },
                '& p, & div, & span': { textAlign: 'left !important', my: 0.5 },
                '& ul': { pl: 3, my: 0.5, listStyleType: 'disc', textAlign: 'left !important' },
                '& ol': { pl: 3, my: 0.5, listStyleType: 'decimal', textAlign: 'left !important' },
                '& li': { mb: 0.25, display: 'list-item', textAlign: 'left !important' },
                '& a': { color: 'primary.main', textDecoration: 'underline' },
              }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.helpText, { FORBID_ATTR: ['style'] }) }}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      id={`question-${question.id}`}
      sx={{
        borderColor: error ? 'error.main' : (isHighlighted ? 'primary.main' : undefined),
        borderWidth: error || isHighlighted ? 2 : undefined,
        borderStyle: error || isHighlighted ? 'solid' : undefined,
        bgcolor: error ? alpha('#ef4444', 0.02) : (isHighlighted ? alpha('#2BC4AD', 0.08) : undefined),
        boxShadow: isHighlighted ? 4 : undefined,
        transition: isHighlighted ? 'all 0.3s ease-in-out' : 'border-color 0.2s ease, background-color 0.2s ease',
        ...(isHighlighted && {
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(43, 196, 173, 0.7)' },
            '50%': { boxShadow: '0 0 0 8px rgba(43, 196, 173, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(43, 196, 173, 0)' },
          },
          animation: 'pulse 1.5s ease-in-out',
        }),
      }}
    >
      {/* Compact padding: xs=12px, sm=14px */}
      <CardContent sx={{ p: { xs: 1.5, sm: 1.75 }, '&:last-child': { pb: { xs: 1.5, sm: 1.75 } } }}>
        {/* Question Header */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {question.text}
            {question.isRequired && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
            )}
          </Typography>
          {question.helpText && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, wordBreak: 'break-word' }}
            >
              {question.helpText}
            </Typography>
          )}
        </Box>

        {/* Question Content */}
        <Box>
          {typeCode === 'OPEN_TEXT' && (
            <OpenTextField
              value={answer.freeText || ''}
              onChange={(value) => onChange({ freeText: value })}
              placeholder={(metadata?.placeholder as string) || 'Type your answer here...'}
              maxLength={(metadata?.maxLength as number) || undefined}
            />
          )}

          {typeCode === 'SINGLE_CHOICE' && (
            <SingleChoiceField
              options={options}
              selectedId={answer.selectedOptionIds?.[0]}
              onChange={(id) => onChange({ selectedOptionIds: id ? [id] : [], isOtherSelected: false })}
              allowOtherText={question.allowOtherText}
              otherTextMode={(metadata?.otherTextMode as 'short' | 'long') || 'short'}
              otherText={answer.otherText}
              onOtherTextChange={(text) => onChange({ otherText: text })}
              isOtherSelected={answer.isOtherSelected}
              onOtherSelect={(selected) => onChange({
                isOtherSelected: selected,
                selectedOptionIds: selected && otherOptionId ? [otherOptionId] : [],
              })}
              error={error}
            />
          )}

          {typeCode === 'MULTIPLE_CHOICE' && (
            <MultipleChoiceField
              options={options}
              selectedIds={answer.selectedOptionIds || []}
              onChange={(ids) => onChange({ selectedOptionIds: ids })}
              minSelections={question.minSelections}
              maxSelections={question.maxSelections}
              allowOtherText={question.allowOtherText}
              otherTextMode={(metadata?.otherTextMode as 'short' | 'long') || 'short'}
              otherText={answer.otherText}
              onOtherTextChange={(text) => onChange({ otherText: text })}
              isOtherSelected={answer.isOtherSelected}
              onOtherSelect={(selected) => {
                const currentIds = answer.selectedOptionIds || [];
                if (selected && otherOptionId) {
                  // Add "Other" option ID
                  onChange({ isOtherSelected: true, selectedOptionIds: [...currentIds, otherOptionId] });
                } else if (!selected && otherOptionId) {
                  // Remove "Other" option ID
                  onChange({ isOtherSelected: false, selectedOptionIds: currentIds.filter(id => id !== otherOptionId) });
                } else {
                  onChange({ isOtherSelected: selected });
                }
              }}
              error={error}
            />
          )}

          {typeCode === 'TRUE_FALSE' && (
            <TrueFalseField
              options={options}
              selectedId={answer.selectedOptionIds?.[0]}
              onChange={(id) => onChange({ selectedOptionIds: id ? [id] : [] })}
              trueLabel={(metadata?.trueLabel as string) || 'Yes'}
              falseLabel={(metadata?.falseLabel as string) || 'No'}
            />
          )}

          {typeCode === 'SCALE5' && (
            <ScaleField
              options={options}
              selectedId={answer.selectedOptionIds?.[0]}
              onChange={(id) => onChange({ selectedOptionIds: id ? [id] : [] })}
              startLabel={(metadata?.startLabel as string) || 'Strongly Disagree'}
              endLabel={(metadata?.endLabel as string) || 'Strongly Agree'}
            />
          )}

          {typeCode === 'RANKED_CHOICE' && (
            <RankedChoiceField
              options={allOptions}
              rankedSelections={answer.rankedSelections || []}
              onChange={(selections) => onChange({ rankedSelections: selections })}
              minSelections={question.minSelections || 1}
              maxSelections={question.maxSelections || options.length}
              allowOtherText={question.allowOtherText}
              otherTextMode={(metadata?.otherTextMode as 'short' | 'long') || 'short'}
              otherText={answer.otherText}
              onOtherTextChange={(text) => onChange({ otherText: text })}
              isOtherSelected={answer.isOtherSelected}
              onOtherSelect={(selected) => onChange({ isOtherSelected: selected })}
              error={error}
            />
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
              <Typography variant="caption">{error}</Typography>
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

// Debounced TextField - uses local state to prevent expensive re-renders on every keystroke
interface DebouncedTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  autoFocus?: boolean;
  label?: string;
  sx?: object;
  showCharCount?: boolean;
  error?: boolean;
  helperText?: string;
}

const DebouncedTextField = memo(function DebouncedTextField({
  value,
  onChange,
  placeholder,
  maxLength,
  multiline = false,
  rows = 1,
  autoFocus = false,
  label,
  sx,
  showCharCount = false,
  error = false,
  helperText,
}: DebouncedTextFieldProps) {
  // Use local state to prevent re-renders of parent on every keystroke
  const [localValue, setLocalValue] = useState(value);
  const charCount = localValue.length;
  const isNearLimit = maxLength && charCount >= maxLength * 0.8;

  // Sync local state when parent value changes (e.g., reset form)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Only propagate changes to parent on blur to avoid expensive recalculations
  const handleBlur = useCallback(() => {
    if (localValue !== value) {
      onChange(localValue);
    }
  }, [localValue, value, onChange]);

  return (
    <Box>
      <TextField
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        inputProps={{ maxLength }}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        size="small"
        fullWidth
        autoFocus={autoFocus}
        label={label}
        error={error}
        helperText={helperText}
        sx={sx}
      />
      {showCharCount && maxLength && (
        <FormHelperText sx={{ textAlign: 'right', color: isNearLimit ? 'warning.main' : undefined, mt: 0.25 }}>
          {charCount}/{maxLength}
        </FormHelperText>
      )}
    </Box>
  );
});

// Open Text Field Component - Memoized with local state for performance
interface OpenTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxLength?: number;
}

const OpenTextField = memo(function OpenTextField({ value, onChange, placeholder, maxLength }: OpenTextFieldProps) {
  return (
    <DebouncedTextField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      maxLength={maxLength}
      multiline
      rows={3}
      showCharCount={!!maxLength}
    />
  );
});

// Single Choice Field Component - Memoized
interface SingleChoiceFieldProps {
  options: QuestionOption[];
  selectedId?: number;
  onChange: (id: number | undefined) => void;
  allowOtherText?: boolean;
  otherTextMode?: 'short' | 'long';
  otherText?: string;
  onOtherTextChange?: (text: string) => void;
  isOtherSelected?: boolean;
  onOtherSelect?: (selected: boolean) => void;
  error?: string;
}

const SingleChoiceField = memo(function SingleChoiceField({
  options,
  selectedId,
  onChange,
  allowOtherText,
  otherTextMode = 'short',
  otherText,
  onOtherTextChange,
  isOtherSelected,
  onOtherSelect,
  error,
}: SingleChoiceFieldProps) {
  const { t } = useSurveyTranslation();

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === 'other') {
      onChange(undefined);
      onOtherSelect?.(true);
    } else {
      // Regular option selected - parent's onChange already sets isOtherSelected: false
      onChange(value ? Number(value) : undefined);
    }
  };

  // Use different labels and placeholders based on mode
  const otherLabel = otherTextMode === 'long'
    ? t('public.other.labelLong', 'Other (please describe):')
    : t('public.other.label', 'Other (please specify):');
  const otherPlaceholder = otherTextMode === 'long'
    ? t('public.other.placeholderLong', 'Describe your answer...')
    : t('public.other.placeholder', 'Type your answer...');

  return (
    <RadioGroup
      value={isOtherSelected ? 'other' : (selectedId ? String(selectedId) : '')}
      onChange={handleRadioChange}
    >
      {options.map((opt) => (
        <FormControlLabel
          key={opt.id}
          value={String(opt.id)}
          control={<Radio size="small" sx={{ p: 0.5 }} />}
          label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{opt.label}</Typography>}
          sx={{
            m: 0,
            py: 0.5,
            px: 1,
            borderRadius: 0.5,
            minHeight: 36, // Compact but still clickable
            '&:hover': { bgcolor: alpha('#14B8A6', 0.04) },
            ...(selectedId === opt.id && !isOtherSelected && { bgcolor: alpha('#14B8A6', 0.08) }),
            // Mobile touch target - slightly larger on touch
            '@media (pointer: coarse)': {
              py: 0.75,
              minHeight: 44,
            },
          }}
        />
      ))}
      {allowOtherText && (
        <Box sx={{ mt: 0.5, width: '100%', overflow: 'hidden' }}>
          <FormControlLabel
            value="other"
            control={<Radio size="small" sx={{ p: 0.5 }} />}
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                {otherLabel}
              </Typography>
            }
            sx={{
              m: 0,
              py: 0.5,
              px: 1,
              borderRadius: 0.5,
              minHeight: 36,
              '&:hover': { bgcolor: alpha('#14B8A6', 0.04) },
              ...(isOtherSelected && { bgcolor: alpha('#14B8A6', 0.08) }),
              '@media (pointer: coarse)': {
                py: 0.75,
                minHeight: 44,
              },
            }}
          />
          {isOtherSelected && (
            <Box sx={{ mt: 1, pl: 4, width: '100%' }}>
              <DebouncedTextField
                value={otherText || ''}
                onChange={(text) => onOtherTextChange?.(text)}
                placeholder={otherPlaceholder}
                autoFocus
                multiline={otherTextMode === 'long'}
                rows={otherTextMode === 'long' ? 3 : 1}
                error={!!error && (!otherText || otherText.trim().length === 0)}
                helperText={error && (!otherText || otherText.trim().length === 0) ? error : undefined}
              />
            </Box>
          )}
        </Box>
      )}
    </RadioGroup>
  );
});

// Multiple Choice Field Component - Memoized
interface MultipleChoiceFieldProps {
  options: QuestionOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  minSelections?: number;
  maxSelections?: number;
  allowOtherText?: boolean;
  otherTextMode?: 'short' | 'long';
  otherText?: string;
  onOtherTextChange?: (text: string) => void;
  isOtherSelected?: boolean;
  onOtherSelect?: (selected: boolean) => void;
  error?: string;
}

const MultipleChoiceField = memo(function MultipleChoiceField({
  options,
  selectedIds,
  onChange,
  minSelections,
  maxSelections,
  allowOtherText,
  otherTextMode = 'short',
  otherText,
  onOtherTextChange,
  isOtherSelected,
  onOtherSelect,
  error,
}: MultipleChoiceFieldProps) {
  const { t } = useSurveyTranslation();

  // Count total selections including "Other" if selected
  const totalSelections = selectedIds.length + (isOtherSelected ? 1 : 0);

  // Use different labels and placeholders based on mode
  const otherLabel = otherTextMode === 'long'
    ? t('public.other.labelLong', 'Other (please describe):')
    : t('public.other.label', 'Other (please specify):');
  const otherPlaceholder = otherTextMode === 'long'
    ? t('public.other.placeholderLong', 'Describe your answer...')
    : t('public.other.placeholder', 'Type your answer...');

  const handleToggle = (optionId: number, checked: boolean) => {
    if (checked) {
      if (maxSelections && totalSelections >= maxSelections) return;
      onChange([...selectedIds, optionId]);
    } else {
      onChange(selectedIds.filter((id) => id !== optionId));
    }
  };

  const handleOtherToggle = (checked: boolean) => {
    if (checked) {
      if (maxSelections && totalSelections >= maxSelections) return;
      onOtherSelect?.(true);
    } else {
      onOtherSelect?.(false);
    }
  };

  return (
    <Box>
      <FormGroup>
        {options.map((opt) => {
          const isSelected = selectedIds.includes(opt.id);
          const isDisabled = !isSelected && maxSelections ? totalSelections >= maxSelections : false;

          return (
            <FormControlLabel
              key={opt.id}
              control={
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={(e) => handleToggle(opt.id, e.target.checked)}
                  disabled={isDisabled}
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{opt.label}</Typography>}
              sx={{
                m: 0,
                py: 0.5,
                px: 1,
                borderRadius: 0.5,
                minHeight: 36, // Compact but still clickable
                opacity: isDisabled ? 0.5 : 1,
                '&:hover': { bgcolor: alpha('#14B8A6', 0.04) },
                ...(isSelected && { bgcolor: alpha('#14B8A6', 0.08) }),
                // Mobile touch target - slightly larger on touch
                '@media (pointer: coarse)': {
                  py: 0.75,
                  minHeight: 44,
                },
              }}
            />
          );
        })}
        {allowOtherText && (
          <Box sx={{ mt: 0.5, width: '100%', overflow: 'hidden' }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isOtherSelected || false}
                  onChange={(e) => handleOtherToggle(e.target.checked)}
                  disabled={!isOtherSelected && maxSelections ? totalSelections >= maxSelections : false}
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {otherLabel}
                </Typography>
              }
              sx={{
                m: 0,
                py: 0.5,
                px: 1,
                borderRadius: 0.5,
                minHeight: 36,
                opacity: !isOtherSelected && maxSelections && totalSelections >= maxSelections ? 0.5 : 1,
                '&:hover': { bgcolor: alpha('#14B8A6', 0.04) },
                ...(isOtherSelected && { bgcolor: alpha('#14B8A6', 0.08) }),
                '@media (pointer: coarse)': {
                  py: 0.75,
                  minHeight: 44,
                },
              }}
            />
            {isOtherSelected && (
              <Box sx={{ mt: 1, pl: 4, width: '100%' }}>
                <DebouncedTextField
                  value={otherText || ''}
                  onChange={(text) => onOtherTextChange?.(text)}
                  placeholder={otherPlaceholder}
                  autoFocus
                  multiline={otherTextMode === 'long'}
                  rows={otherTextMode === 'long' ? 3 : 1}
                  error={!!error && (!otherText || otherText.trim().length === 0)}
                  helperText={error && (!otherText || otherText.trim().length === 0) ? error : undefined}
                />
              </Box>
            )}
          </Box>
        )}
      </FormGroup>
      {(minSelections || maxSelections) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {minSelections && maxSelections
              ? `Select ${minSelections}-${maxSelections}`
              : minSelections
              ? `Min: ${minSelections}`
              : `Max: ${maxSelections}`}
          </Typography>
          <Chip
            label={`${totalSelections} selected`}
            size="small"
            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
            color={totalSelections >= (minSelections || 0) ? 'primary' : 'default'}
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
});

// True/False Field Component - Memoized
interface TrueFalseFieldProps {
  options: QuestionOption[];
  selectedId?: number;
  onChange: (id: number | undefined) => void;
  trueLabel: string;
  falseLabel: string;
}

const TrueFalseField = memo(function TrueFalseField({ options, selectedId, onChange, trueLabel, falseLabel }: TrueFalseFieldProps) {
  // TRUE_FALSE questions have 2 options with numericValue: 1 (true) and 0 (false)
  // Find options by numericValue to ensure correct mapping regardless of order
  const trueOption = options.find(opt => opt.numericValue === 1) || options[0];
  const falseOption = options.find(opt => opt.numericValue === 0) || options[1];

  // Defensive check: if no options exist, log detailed error for debugging
  if (!trueOption || !falseOption) {
    console.error('TrueFalseField: Missing options for TRUE_FALSE question', {
      options,
      optionsLength: options?.length,
      trueOption,
      falseOption,
      firstOptionStructure: options?.[0] ? Object.keys(options[0]) : 'no options',
      numericValues: options?.map(opt => ({ id: opt.id, label: opt.label, numericValue: opt.numericValue }))
    });
  }

  const handleTrueClick = () => {
    if (trueOption?.id) {
      onChange(trueOption.id);
    } else {
      console.error('TrueFalseField: Cannot select true option - missing option data');
    }
  };

  const handleFalseClick = () => {
    if (falseOption?.id) {
      onChange(falseOption.id);
    } else {
      console.error('TrueFalseField: Cannot select false option - missing option data');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Button
        variant={selectedId === trueOption?.id ? 'contained' : 'outlined'}
        size="small"
        onClick={handleTrueClick}
        disabled={!trueOption}
        sx={{
          flex: 1,
          py: 1,
          minHeight: 44, // Minimum touch target
          // Mobile touch target optimization
          '@media (pointer: coarse)': {
            minHeight: 48,
            py: 1.25,
          },
        }}
      >
        {trueLabel}
      </Button>
      <Button
        variant={selectedId === falseOption?.id ? 'contained' : 'outlined'}
        size="small"
        onClick={handleFalseClick}
        disabled={!falseOption}
        sx={{
          flex: 1,
          py: 1,
          minHeight: 44, // Minimum touch target
          // Mobile touch target optimization
          '@media (pointer: coarse)': {
            minHeight: 48,
            py: 1.25,
          },
        }}
      >
        {falseLabel}
      </Button>
    </Box>
  );
});

// Scale Field Component - Memoized
interface ScaleFieldProps {
  options: QuestionOption[];
  selectedId?: number;
  onChange: (id: number | undefined) => void;
  startLabel: string;
  endLabel: string;
}

const ScaleField = memo(function ScaleField({ options, selectedId, onChange, startLabel, endLabel }: ScaleFieldProps) {
  const sortedOptions = [...options].sort((a, b) => (a.numericValue || 0) - (b.numericValue || 0));

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1,
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            maxWidth: '45%',
            wordBreak: 'break-word',
          }}
        >
          {startLabel}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            maxWidth: '45%',
            wordBreak: 'break-word',
            textAlign: 'right',
          }}
        >
          {endLabel}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 1 },
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        {sortedOptions.map((opt, idx) => {
          const isSelected = selectedId === opt.id;
          return (
            <Button
              key={opt.id}
              variant={isSelected ? 'contained' : 'outlined'}
              onClick={() => onChange(opt.id)}
              className="scale-button"
              sx={{
                minWidth: { xs: 40, sm: 44 },
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: '50%',
                p: 0,
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                flexShrink: 0,
                // Mobile touch target optimization
                '@media (pointer: coarse)': {
                  minWidth: 44,
                  width: 44,
                  height: 44,
                },
              }}
            >
              {opt.numericValue || idx + 1}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
});

// Helper to find the "Other" option from question options
const getOtherOptionId = (options: QuestionOption[]): number | undefined => {
  return options.find(o => o.isOtherOption)?.id;
};

// Ranked Choice Field Component
interface RankedChoiceFieldProps {
  options: QuestionOption[];
  rankedSelections: { optionId: number; rank: number }[];
  onChange: (selections: { optionId: number; rank: number }[]) => void;
  minSelections: number;
  maxSelections: number;
  allowOtherText?: boolean;
  otherTextMode?: 'short' | 'long';
  otherText?: string;
  onOtherTextChange?: (text: string) => void;
  isOtherSelected?: boolean;
  onOtherSelect?: (selected: boolean) => void;
  error?: string;
}

interface SortableRankedItemProps {
  id: number;
  label: string;
  rank: number;
  totalItems: number;
  isTouchDevice: boolean;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

function SortableRankedItem({
  id,
  label,
  rank,
  totalItems,
  isTouchDevice,
  onRemove,
  onMoveUp,
  onMoveDown,
}: SortableRankedItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease, opacity 200ms ease',
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const isFirst = rank === 1;
  const isLast = rank === totalItems;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className="ranking-item"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        p: { xs: 1, sm: 1.5 },
        borderRadius: 1,
        border: 2,
        borderColor: isDragging ? 'primary.main' : alpha('#14B8A6', 0.3),
        bgcolor: alpha('#14B8A6', 0.04),
        mb: 1,
        boxShadow: isDragging ? '0 4px 12px rgba(79, 70, 229, 0.2)' : 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: alpha('#14B8A6', 0.08),
        },
      }}
    >
      {/* Show drag handle on desktop, up/down buttons on touch devices */}
      {isTouchDevice ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={onMoveUp}
            disabled={isFirst}
            className="ranking-reorder-btn"
            sx={{
              p: 0.5,
              minWidth: 32,
              minHeight: 32,
              color: isFirst ? 'action.disabled' : 'primary.main',
              bgcolor: isFirst ? 'transparent' : alpha('#14B8A6', 0.1),
              '&:hover': { bgcolor: isFirst ? 'transparent' : alpha('#14B8A6', 0.2) },
              // Touch target optimization
              '@media (pointer: coarse)': {
                minWidth: 40,
                minHeight: 40,
              },
            }}
            aria-label="Move up"
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onMoveDown}
            disabled={isLast}
            className="ranking-reorder-btn"
            sx={{
              p: 0.5,
              minWidth: 32,
              minHeight: 32,
              color: isLast ? 'action.disabled' : 'primary.main',
              bgcolor: isLast ? 'transparent' : alpha('#14B8A6', 0.1),
              '&:hover': { bgcolor: isLast ? 'transparent' : alpha('#14B8A6', 0.2) },
              // Touch target optimization
              '@media (pointer: coarse)': {
                minWidth: 40,
                minHeight: 40,
              },
            }}
            aria-label="Move down"
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ) : (
        <IconButton
          size="small"
          {...attributes}
          {...listeners}
          className="ranking-drag-handle"
          sx={{
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            p: 0.75,
            minWidth: 36,
            minHeight: 36,
            color: 'primary.main',
            bgcolor: alpha('#14B8A6', 0.1),
            '&:hover': { bgcolor: alpha('#14B8A6', 0.2) },
          }}
          aria-label="Drag to reorder"
        >
          <DragIndicatorIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 14,
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(79, 70, 229, 0.3)',
        }}
      >
        {rank}
      </Box>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      <IconButton
        size="small"
        onClick={onRemove}
        className="ranking-remove-btn"
        sx={{
          color: 'error.main',
          p: 0.75,
          minWidth: 36,
          minHeight: 36,
          opacity: 0.7,
          '&:hover': { opacity: 1, bgcolor: alpha('#ef4444', 0.1) },
          // Mobile touch target optimization
          '@media (pointer: coarse)': {
            minWidth: 44,
            minHeight: 44,
            p: 1,
            opacity: 1,
          },
        }}
        aria-label="Remove from ranking"
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

// RankedChoiceField - Memoized
const RankedChoiceField = memo(function RankedChoiceField({
  options,
  rankedSelections,
  onChange,
  minSelections,
  maxSelections,
  allowOtherText,
  otherTextMode = 'short',
  otherText,
  onOtherTextChange,
  // isOtherSelected is tracked by parent for form state, we use isOtherInRanking locally
  onOtherSelect,
  error,
}: RankedChoiceFieldProps) {
  const { t } = useSurveyTranslation();
  // Detect touch device for mobile-optimized UI
  const isTouchDevice = useIsTouchDevice();

  // Get the actual "Other" option ID from the question's options (if it exists)
  const otherOptionId = getOtherOptionId(options);

  // Check if "Other" option is currently in the ranked selections
  const isOtherInRanking = otherOptionId ? rankedSelections.some((s) => s.optionId === otherOptionId) : false;

  // Use different labels and placeholders based on mode
  const otherLabel = otherTextMode === 'long'
    ? t('public.other.labelLong', 'Other (please describe):')
    : t('public.other.label', 'Other (please specify):');
  const otherPlaceholder = otherTextMode === 'long'
    ? t('public.other.placeholderLong', 'Describe your answer...')
    : t('public.other.placeholder', 'Type your answer...');

  const sensors = useSortableSensors();

  const selectedOptionIds = rankedSelections.map((s) => s.optionId);
  // Filter out selected options AND the "Other" option (shown separately via the dedicated button)
  const unselectedOptions = options.filter((o) => !selectedOptionIds.includes(o.id) && !o.isOtherOption);

  // Determine if selection requirement is met
  const isRequirementMet = rankedSelections.length >= minSelections;
  const isMaxReached = rankedSelections.length >= maxSelections;

  const handleSelect = (optionId: number) => {
    if (rankedSelections.length >= maxSelections) return;

    const newSelections = [
      ...rankedSelections,
      { optionId, rank: rankedSelections.length + 1 },
    ];
    onChange(newSelections);
  };

  // Handle selecting the "Other" option (adds it to ranking)
  const handleSelectOther = () => {
    if (rankedSelections.length >= maxSelections) return;
    if (isOtherInRanking) return;
    if (!otherOptionId) return; // No "Other" option available

    const newSelections = [
      ...rankedSelections,
      { optionId: otherOptionId, rank: rankedSelections.length + 1 },
    ];
    onChange(newSelections);
    onOtherSelect?.(true);
  };

  const handleRemove = (optionId: number) => {
    const newSelections = rankedSelections
      .filter((s) => s.optionId !== optionId)
      .map((s, idx) => ({ ...s, rank: idx + 1 }));
    onChange(newSelections);

    // If removing the "Other" option, also clear the isOtherSelected state
    if (otherOptionId && optionId === otherOptionId) {
      onOtherSelect?.(false);
    }
  };

  // Move item up in ranking (decrease rank number)
  const handleMoveUp = useCallback((optionId: number) => {
    const currentIndex = rankedSelections.findIndex((s) => s.optionId === optionId);
    if (currentIndex <= 0) return;

    const reordered = arrayMove(rankedSelections, currentIndex, currentIndex - 1).map(
      (s, idx) => ({ ...s, rank: idx + 1 })
    );
    onChange(reordered);
  }, [rankedSelections, onChange]);

  // Move item down in ranking (increase rank number)
  const handleMoveDown = useCallback((optionId: number) => {
    const currentIndex = rankedSelections.findIndex((s) => s.optionId === optionId);
    if (currentIndex >= rankedSelections.length - 1) return;

    const reordered = arrayMove(rankedSelections, currentIndex, currentIndex + 1).map(
      (s, idx) => ({ ...s, rank: idx + 1 })
    );
    onChange(reordered);
  }, [rankedSelections, onChange]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rankedSelections.findIndex((s) => s.optionId === active.id);
      const newIndex = rankedSelections.findIndex((s) => s.optionId === over.id);

      const reordered = arrayMove(rankedSelections, oldIndex, newIndex).map(
        (s, idx) => ({ ...s, rank: idx + 1 })
      );

      onChange(reordered);
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Task 1.3.1: Large, Prominent Selection Counter at TOP */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 },
          mb: 2,
          p: { xs: 1, sm: 1.5 },
          borderRadius: 1.5,
          bgcolor: isRequirementMet ? alpha('#10b981', 0.08) : alpha('#f59e0b', 0.08),
          border: 2,
          borderColor: isRequirementMet ? 'success.main' : 'warning.main',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              bgcolor: isRequirementMet ? 'success.main' : 'warning.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isRequirementMet ? (
              <CheckIcon sx={{ fontSize: 20 }} />
            ) : (
              <AddIcon sx={{ fontSize: 20 }} />
            )}
          </Box>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: isRequirementMet ? 'success.dark' : 'warning.dark',
              }}
            >
              {rankedSelections.length} of {minSelections === maxSelections ? minSelections : `${minSelections}-${maxSelections}`} selected
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isRequirementMet
                ? isMaxReached
                  ? isTouchDevice
                    ? 'Maximum reached - use arrows to reorder'
                    : 'Maximum reached - drag to reorder'
                  : isTouchDevice
                    ? 'Requirement met - you can add more or use arrows to reorder'
                    : 'Requirement met - you can add more or reorder'
                : `Select ${minSelections - rankedSelections.length} more to continue`}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={isRequirementMet ? 'Complete' : 'In Progress'}
          size="medium"
          color={isRequirementMet ? 'success' : 'warning'}
          sx={{
            fontWeight: 600,
            px: 1,
          }}
        />
      </Box>

      {/* Instructions - different for touch vs desktop */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
        sx={{
          mb: 2,
          py: 0.75,
          '& .MuiAlert-message': { py: 0 },
          bgcolor: alpha('#3b82f6', 0.05),
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {isTouchDevice
            ? 'Tap to select options, then use arrows to reorder (1 = most important)'
            : 'Click to select options, then drag to rank them (1 = most important)'}
        </Typography>
      </Alert>

      {/* Task 1.3.2: Selected & Ranked Section with Visual Distinction */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 1.5,
          bgcolor: alpha('#14B8A6', 0.04),
          border: 1,
          borderColor: alpha('#14B8A6', 0.15),
          borderLeft: 4,
          borderLeftColor: 'primary.main',
          minHeight: rankedSelections.length === 0 ? 80 : 'auto',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease-in-out',
        }}
        className="ranking-selected-area"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.75rem',
            }}
          >
            Your Rankings
          </Typography>
          {rankedSelections.length > 0 && (
            <Chip
              label={rankedSelections.length}
              size="small"
              color="primary"
              sx={{
                height: 20,
                minWidth: 20,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 700 },
              }}
            />
          )}
        </Box>

        {rankedSelections.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              py: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Click options below to add them here
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={rankedSelections.map((s) => s.optionId)}
              strategy={verticalListSortingStrategy}
            >
              {/* Task 1.3.3: Animate items with Grow component */}
              {rankedSelections.map((selection, index) => {
                const option = options.find((o) => o.id === selection.optionId);
                const isOther = option?.isOtherOption ?? false;
                const label = isOther
                  ? `${otherLabel}: ${otherText || ''}`
                  : (option?.label || '');
                return (
                  <Grow
                    key={selection.optionId}
                    in={true}
                    timeout={200 + index * 50}
                    style={{ transformOrigin: '0 0' }}
                  >
                    <Box>
                      <SortableRankedItem
                        id={selection.optionId}
                        label={label}
                        rank={selection.rank}
                        totalItems={rankedSelections.length}
                        isTouchDevice={isTouchDevice}
                        onRemove={() => handleRemove(selection.optionId)}
                        onMoveUp={() => handleMoveUp(selection.optionId)}
                        onMoveDown={() => handleMoveDown(selection.optionId)}
                      />
                    </Box>
                  </Grow>
                );
              })}
            </SortableContext>
          </DndContext>
        )}

        {/* Text input for "Other" option when it's in the ranking */}
        {isOtherInRanking && (
          <Box sx={{ mt: 1.5, pl: 0, width: '100%' }}>
            <DebouncedTextField
              value={otherText || ''}
              onChange={(text) => onOtherTextChange?.(text)}
              placeholder={otherPlaceholder}
              autoFocus
              label={otherLabel}
              multiline={otherTextMode === 'long'}
              rows={otherTextMode === 'long' ? 3 : 1}
              error={!!error && (!otherText || otherText.trim().length === 0)}
              helperText={error && (!otherText || otherText.trim().length === 0) ? error : undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Available Options Section */}
      {/* Show when there are unselected options OR when "Other" is available but not selected */}
      {(unselectedOptions.length > 0 || (allowOtherText && !isOtherInRanking)) && !isMaxReached && (
        <Box className="ranking-available-area">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
              }}
            >
              Available Options
            </Typography>
            <Chip
              label={unselectedOptions.length + (allowOtherText && !isOtherInRanking ? 1 : 0)}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                minWidth: 20,
                '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {unselectedOptions.map((opt, index) => (
              <Grow
                key={opt.id}
                in={true}
                timeout={100 + index * 30}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  onClick={() => handleSelect(opt.id)}
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.25,
                    px: 2,
                    minHeight: 44, // Minimum touch target
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: alpha('#14B8A6', 0.3),
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderStyle: 'solid',
                      borderColor: 'primary.main',
                      bgcolor: alpha('#14B8A6', 0.04),
                      transform: 'translateX(4px)',
                    },
                    // Mobile touch target optimization
                    '@media (pointer: coarse)': {
                      minHeight: 48,
                      py: 1.5,
                    },
                  }}
                >
                  {opt.label}
                </Button>
              </Grow>
            ))}
            {/* "Other" option button - only show if allowOtherText is enabled and not already in ranking */}
            {allowOtherText && !isOtherInRanking && (
              <Grow
                in={true}
                timeout={100 + unselectedOptions.length * 30}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  onClick={handleSelectOther}
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    justifyContent: 'flex-start',
                    py: 1.25,
                    px: 2,
                    minHeight: 44,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: alpha('#f59e0b', 0.4),
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: 'warning.dark',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderStyle: 'solid',
                      borderColor: 'warning.main',
                      bgcolor: alpha('#f59e0b', 0.08),
                      transform: 'translateX(4px)',
                    },
                    '@media (pointer: coarse)': {
                      minHeight: 48,
                      py: 1.5,
                    },
                  }}
                >
                  {otherLabel}
                </Button>
              </Grow>
            )}
          </Box>
        </Box>
      )}

      {/* Max reached message */}
      {isMaxReached && unselectedOptions.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: alpha('#6b7280', 0.08),
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Maximum of {maxSelections} options selected. Remove an option to select a different one.
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default PublicSurvey;
